/** Open Pulse/Daton from iQ setup with SSO handoff (avoids Daton AuthGuard → /logout). */

import { useCallback, useEffect, useMemo, useState } from "react";
import { POSTHOG } from "@/constants";
import { posthog } from "@/config/posthog.config";
import { useAuth } from "@/contexts/AuthContext";
import type { IUserInfo } from "@/services/apis/authApi";
import {
  openOnboardingProduct,
  type ProductHandoffSession,
  type ProductWindow,
} from "@/utils/onboardingProductLinks";

/** Firebase ID tokens last an hour; re-read well inside that so a click never hands off a dead one. */
const TOKEN_REFRESH_MS = 10 * 60 * 1000;

export function useOnboardingProductOpen() {
  const { sarasGlobal, userDetails, currentUser } = useAuth();
  const [freshToken, setFreshToken] = useState<string | null>(null);

  // `sarasGlobal.token` is whatever Accounts wrote at login and is never refreshed, so after an
  // hour Daton's `verifyfiretoken` rejects it ("Firebase ID token has expired"). The Firebase
  // user returns a cached token while it is valid and refreshes it near expiry. Read it ahead of
  // the click so the click stays synchronous and `window.open` keeps its user activation.
  useEffect(() => {
    // Not every `currentUser` is a full Firebase user (test and local-dev doubles), so check the
    // method rather than assume it.
    if (!currentUser || typeof currentUser.getIdToken !== "function") {
      setFreshToken(null);
      return;
    }
    let cancelled = false;
    const refresh = () => {
      currentUser
        .getIdToken()
        .then((token) => {
          if (!cancelled) setFreshToken(token);
        })
        .catch(() => {
          // Keep the last token; the cookie token below is the fallback.
        });
    };
    refresh();
    const timer = window.setInterval(refresh, TOKEN_REFRESH_MS);
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", refresh);
      window.removeEventListener("focus", refresh);
    };
  }, [currentUser]);

  const session: ProductHandoffSession = useMemo(
    () => ({
      firebaseToken: freshToken ?? sarasGlobal?.token ?? null,
      user: (userDetails ?? sarasGlobal?.user ?? null) as IUserInfo | null,
    }),
    [freshToken, sarasGlobal, userDetails],
  );

  return useCallback(
    (destinationUrl: string, product: ProductWindow) => {
      const opened = openOnboardingProduct(destinationUrl, product, session);
      // Not `usePostHogTracking`: that reads the router, and this hook runs outside one in tests.
      posthog?.capture(POSTHOG.EVENTS.ONBOARDING_PRODUCT_OPENED, {
        product,
        is_blocked: !opened,
      });
      return opened;
    },
    [session],
  );
}
